import React, { useEffect, useMemo, useState } from 'react';
import './LoanBackfill.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL

function todayLocalInputValue() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
}

function money(value) {
  const numeric = Number(value ?? 0);
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getErrorMessage(error) {
  if (error?.details?.errors) {
    const first = Object.values(error.details.errors).flat().filter(Boolean)[0];
    if (first) return first;
  }
  return error?.message || 'Request failed';
}

function LoanBackfill() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(() => localStorage.getItem('mtc_backfill_token') || '');
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('mtc_backfill_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [employers, setEmployers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [amount, setAmount] = useState('');
  const [termMonths, setTermMonths] = useState('1');
  const [loanDateTime, setLoanDateTime] = useState(todayLocalInputValue());
  const [purpose, setPurpose] = useState('Temporary admin loan backfill');
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const selectedEmployer = useMemo(
    () => employers.find((employer) => employer.id === selectedEmployerId) || null,
    [employers, selectedEmployerId]
  );

  const selectedEmployee = useMemo(
    () => employees.find((employee) => employee.id === selectedEmployeeId) || null,
    [employees, selectedEmployeeId]
  );

  async function api(path, options = {}) {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) {
      throw body;
    }
    return body.data;
  }

  async function handleLogin(event) {
    event.preventDefault();
    setLoading('login');
    setError('');
    setResult(null);

    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const body = await response.json();
      if (!response.ok || body.success === false) throw body;

      if (!['admin', 'superadmin'].includes(body.data?.user?.role)) {
        throw new Error('This login is not an admin account.');
      }

      setToken(body.data.access_token);
      setUser(body.data.user);
      localStorage.setItem('mtc_backfill_token', body.data.access_token);
      localStorage.setItem('mtc_backfill_user', JSON.stringify(body.data.user));
      setPassword('');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading('');
    }
  }

  function handleLogout() {
    setToken('');
    setUser(null);
    setEmployers([]);
    setEmployees([]);
    setSelectedEmployerId('');
    setSelectedEmployeeId('');
    setResult(null);
    localStorage.removeItem('mtc_backfill_token');
    localStorage.removeItem('mtc_backfill_user');
  }

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    async function loadEmployers() {
      setLoading('employers');
      setError('');
      try {
        const data = await api('/admin/temporary-loan-backfill/employers');
        if (!cancelled) setEmployers(data);
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading('');
      }
    }
    loadEmployers();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!selectedEmployerId) {
      setEmployees([]);
      setSelectedEmployeeId('');
      return;
    }

    let cancelled = false;
    async function loadEmployees() {
      setLoading('employees');
      setError('');
      setResult(null);
      setSelectedEmployeeId('');
      try {
        const data = await api(
          `/admin/temporary-loan-backfill/employers/${selectedEmployerId}/employees`
        );
        if (!cancelled) setEmployees(data);
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading('');
      }
    }
    loadEmployees();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployerId]);

  async function handleCreateLoan(event) {
    event.preventDefault();
    setLoading('create');
    setError('');
    setResult(null);

    try {
      const created = await api('/admin/temporary-loan-backfill', {
        method: 'POST',
        body: JSON.stringify({
          employee_id: selectedEmployeeId,
          amount: Number(amount),
          term_months: Number(termMonths),
          loan_datetime: new Date(loanDateTime).toISOString(),
          purpose,
        }),
      });
      setResult(created);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading('');
    }
  }

  return (
    <div className="backfill-page">
      <section className="backfill-top">
        <div>
          <p className="backfill-kicker">Temporary Admin Utility</p>
          <h1>Cash Pickup Loan Backfill</h1>
        </div>
        {user && (
          <button className="backfill-secondary" type="button" onClick={handleLogout}>
            Sign out
          </button>
        )}
      </section>

      {error && (
        <div className="backfill-alert" role="alert">
          {error}
        </div>
      )}

      {!token ? (
        <form className="backfill-panel backfill-login" onSubmit={handleLogin}>
          <label>
            Admin email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <button type="submit" disabled={loading === 'login'}>
            {loading === 'login' ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      ) : (
        <div className="backfill-layout">
          <section className="backfill-panel">
            <div className="backfill-panel-header">
              <h2>Employers</h2>
              <span>{loading === 'employers' ? 'Loading' : employers.length}</span>
            </div>
            <div className="backfill-list">
              {employers.map((employer) => (
                <button
                  className={employer.id === selectedEmployerId ? 'is-selected' : ''}
                  key={employer.id}
                  type="button"
                  onClick={() => setSelectedEmployerId(employer.id)}
                >
                  <strong>{employer.company_name}</strong>
                  <span>{employer.status}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="backfill-panel">
            <div className="backfill-panel-header">
              <h2>Employees</h2>
              <span>{loading === 'employees' ? 'Loading' : employees.length}</span>
            </div>
            <div className="backfill-list">
              {employees.map((employee) => {
                const employeeUser = Array.isArray(employee.user) ? employee.user[0] : employee.user;
                return (
                  <button
                    className={employee.id === selectedEmployeeId ? 'is-selected' : ''}
                    key={employee.id}
                    type="button"
                    onClick={() => setSelectedEmployeeId(employee.id)}
                  >
                    <strong>{employeeUser?.full_name || employee.employee_number || 'Unnamed'}</strong>
                    <span>
                      {employee.status} · salary ${money(employee.monthly_salary)}
                    </span>
                    {employee.latest_loan && (
                      <em>
                        Latest loan: {employee.latest_loan.status} ${money(employee.latest_loan.principal)}
                      </em>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="backfill-panel">
            <div className="backfill-panel-header">
              <h2>Loan</h2>
              <span>{selectedEmployer?.company_name || 'No employer'}</span>
            </div>

            {selectedEmployee ? (
              <form className="backfill-form" onSubmit={handleCreateLoan}>
                <div className="backfill-employee-summary">
                  <strong>
                    {(Array.isArray(selectedEmployee.user)
                      ? selectedEmployee.user[0]
                      : selectedEmployee.user
                    )?.full_name || selectedEmployee.employee_number}
                  </strong>
                  <span>{selectedEmployee.department || 'No department'}</span>
                </div>

                <label>
                  Amount USD
                  <input
                    type="number"
                    min="10"
                    step="0.01"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    required
                  />
                </label>

                <label>
                  Term
                  <select value={termMonths} onChange={(event) => setTermMonths(event.target.value)}>
                    <option value="1">1 month</option>
                    <option value="2">2 months</option>
                  </select>
                </label>

                <label>
                  Loan date and time
                  <input
                    type="datetime-local"
                    value={loanDateTime}
                    onChange={(event) => setLoanDateTime(event.target.value)}
                    required
                  />
                </label>

                <label>
                  Purpose
                  <textarea
                    rows="3"
                    value={purpose}
                    onChange={(event) => setPurpose(event.target.value)}
                  />
                </label>

                <button type="submit" disabled={loading === 'create'}>
                  {loading === 'create' ? 'Creating...' : 'Create and disburse'}
                </button>
              </form>
            ) : (
              <div className="backfill-empty">Select an employee.</div>
            )}

            {result && (
              <div className="backfill-result">
                <p>Pickup code</p>
                <strong>{result.pickup_code}</strong>
                <dl>
                  <div>
                    <dt>Loan ID</dt>
                    <dd>{result.loan.id}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>
                      {result.loan.status} · {result.loan.disbursement_status}
                    </dd>
                  </div>
                  <div>
                    <dt>Total</dt>
                    <dd>${money(result.loan.total_amount)}</dd>
                  </div>
                </dl>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export default LoanBackfill;
